import { logError, logMessage } from '@core/logging';
import { getCookie } from '@core/storage';
import { getLocalStorage } from '@core/storage/getStorage';
import { getRawItem } from '@core/storage/localStorage';
/**
 * Gets cookie value with localStorage fallback, ensuring consistent behavior
 */
export function getCookieWithFallback(key) {
    try {
        // Try to get from cookie first
        const fetchedCookie = getCookie(key);
        if (fetchedCookie) {
            try {
                // Try to parse as JSON (new format)
                const parsedResult = JSON.parse(fetchedCookie);
                // Validate the parsed result has the expected structure
                if (parsedResult &&
                    typeof parsedResult === 'object' &&
                    'value' in parsedResult) {
                    return parsedResult;
                }
            }
            catch (e) {
                // Log JSON parsing errors for debugging
                logMessage('getCookieWithFallback: JSON parse failed, treating as raw string', {
                    captureContext: {
                        extra: {
                            key,
                            fetchedCookie,
                            error: e instanceof Error ? e.message : String(e),
                        },
                    },
                });
                // If JSON parsing fails, treat as raw string value (legacy format)
                return { value: fetchedCookie };
            }
        }
        // Fallback to localStorage only if cookie is truly unavailable
        try {
            const localStorageResult = getRawItem(key);
            // Handle object format (standard case)
            if (localStorageResult &&
                typeof localStorageResult === 'object' &&
                'value' in localStorageResult) {
                return localStorageResult;
            }
            // If getRawItem returns undefined, check if there's a raw string directly in localStorage
            // This handles legacy data that might have been stored as plain strings
            if (!localStorageResult) {
                const rawLocalStorageValue = getLocalStorage().getItem(key);
                if (rawLocalStorageValue) {
                    return { value: rawLocalStorageValue };
                }
            }
        }
        catch (localStorageError) {
            // If localStorage also fails, we'll return undefined
        }
        return undefined;
    }
    catch (error) {
        logError(error);
        return undefined;
    }
}
